import type { ReactNode } from 'react';
import { Clock, AlertTriangle } from 'lucide-react';
import { formatIstTime } from '../lib/time';
import { shiftHue, type VisitShiftGroup } from '../lib/visitShifts';

const fmtMin = (m?: number | null) => (m == null ? null : `${Math.floor(m / 60)}h ${m % 60}m`);


/**
 * The strip at the top of a shift's box. `withTimes` prints the shift's punch-in → punch-out and hours —
 * turn it OFF where the shifts are already listed with their times on the same screen.
 */
export function ShiftGroupHeader({ group, withTimes = true, right, added = 0 }: {
  group: VisitShiftGroup<unknown>; withTimes?: boolean; right?: ReactNode;
  /** Dealers / farmers registered in this shift — field work that is not a visit, counted beside the visits. */
  added?: number;
}) {
  const hue = shiftHue(group.no);
  const n = group.visits.length;
  const visitsText = n ? `${n} visit${n === 1 ? '' : 's'}` : 'no visits';
  const count = added ? `${n ? `${visitsText} · ` : ''}${added} added` : visitsText;
  const s = group.shift;
  const running = !!s && !s.checkOutAt;
  return (
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 px-3 py-2 type-caption" style={{ background: `${hue}14`, borderBottom: group.visits.length || added ? `1px solid ${hue}40` : undefined }}>
      {group.no == null ? (
        <>
          <AlertTriangle className="size-4 shrink-0" style={{ color: hue }} />
          <span className="type-body-strong" style={{ color: hue }}>Between shifts</span>
          <span className="text-subtle">made while punched out</span>
        </>
      ) : (
        <>
          <span className="grid size-6 shrink-0 place-items-center rounded-full type-caption-strong text-white" style={{ background: hue }}>{group.no}</span>
          <span className="type-body-strong text-content">Shift {group.no}</span>
          {withTimes && s && (
            <span className="inline-flex items-center gap-1 tabular-nums text-subtle">
              <Clock className="size-3.5" />
              {formatIstTime(s.checkInAt)} – {running ? 'now' : formatIstTime(s.checkOutAt)}
              {!running && fmtMin(s.workedMin) ? ` · ${fmtMin(s.workedMin)}` : ''}
            </span>
          )}
          {running && <span className="rounded-full bg-sky-500/15 px-2 py-0.5 text-sky-700 dark:text-sky-300">on duty</span>}
        </>
      )}
      <span className="ml-auto rounded-full px-2 py-0.5 font-medium" style={{ background: `${hue}1f`, color: hue }}>{count}</span>
      {right}
    </div>
  );
}

/**
 * ONE SHIFT, ONE BOX: a coloured left edge, the header strip, then that shift's visits inside it. A
 * header band alone sat on the same background as the rows around it, so where one shift ended and the
 * next began had to be worked out by reading. An empty shift collapses to its header.
 */
export function ShiftGroupBox({ group, withTimes = true, children, bodyClassName, added = 0, addedList }: {
  group: VisitShiftGroup<unknown>; withTimes?: boolean; children?: ReactNode; bodyClassName?: string;
  /** How many dealers / farmers were registered in this shift, and the list of them (rendered after the visits). */
  added?: number; addedList?: ReactNode;
}) {
  const hue = shiftHue(group.no);
  return (
    <section
      className="overflow-hidden rounded-card border bg-surface"
      // A FULL boundary in the shift's own colour — all four sides — so each shift reads as one object.
      style={{ border: `2px solid ${hue}` }}
      aria-label={group.no == null ? 'Visits between shifts' : `Shift ${group.no}`}
    >
      <ShiftGroupHeader group={group} withTimes={withTimes} added={added} />
      {group.visits.length > 0 && <div className={bodyClassName ?? 'flex flex-col gap-2 p-2.5'}>{children}</div>}
      {added > 0 && addedList ? <div className="px-2.5 pb-2.5 pt-1">{addedList}</div> : null}
    </section>
  );
}

/**
 * "Shift 2 of 3" — which shift of the rep's day a visit was made in, in that shift's colour (the same
 * colour its box wears on My Field Day and in the attendance record). Nothing on a one-shift day: a mark
 * that reads "Shift 1 of 1" on every row is noise. A visit no shift covers reads "Between shifts", amber.
 */
export function ShiftChip({ no, count, className }: { no?: number | null; count?: number | null; className?: string }) {
  if (!count || count < 2) return null;
  const hue = shiftHue(no ?? null);
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 type-caption font-medium ${className ?? ''}`}
      style={{ background: `${hue}1a`, color: hue, boxShadow: `inset 0 0 0 1px ${hue}55` }}
      title={no ? `Made in shift ${no} of the ${count} that day` : 'Made while punched out — between two shifts'}
    >
      {no ? <Clock className="size-3" /> : <AlertTriangle className="size-3" />}
      {no ? `Shift ${no} of ${count}` : 'Between shifts'}
    </span>
  );
}
